import React from 'react';
import type { FormInputProps } from '../FormProps';
import { ErrorMessage } from '~/components/form-fields/ErrorMessage';
import { Label } from '~/components/form-fields/Label';
import { FAIcon } from '~/components/utils/FAIcons';
import clsx from 'clsx';

type TextAreaProps = Pick<
	FormInputProps,
	| 'autoComplete'
	| 'autoFocus'
	| 'className'
	| 'defaultValue'
	| 'disabled'
	| 'errorMessage'
	| 'id'
	| 'labelClassName'
	| 'labelText'
	| 'name'
	| 'onKeyDown'
	| 'placeholder'
	| 'register'
	| 'registerOptions'
	| 'required'
	| 'rows'
	| 'subLabel'
> & {
	gridCellStyling?: boolean;
	subLabelClassName?: string;
	subLabelIcon?: string;
	button?: React.ReactNode;
};

export const TextArea = ({
	autoComplete = 'off',
	autoFocus,
	className,
	defaultValue,
	disabled,
	errorMessage,
	gridCellStyling = false,
	id,
	labelClassName,
	labelText,
	subLabel,
	subLabelClassName,
	subLabelIcon,
	name,
	onKeyDown,
	placeholder,
	register,
	registerOptions,
	required,
	rows,
	button,
}: TextAreaProps) => {
	const baselineClassName = gridCellStyling ? 'border-none' : 'shadow-xs border border-gray-400 rounded-md mt-1';
	return (
		<div className="w-full">
			{labelText && (
				<div className="flex items-center gap-2">
					<Label id={id} className={labelClassName} labelText={labelText} />
					{button && button}
				</div>
			)}
			{subLabel && (
				<p
					className={clsx(`text-sm mb-1 ${subLabelClassName ?? labelClassName}`, {
						'text-gray-700': !subLabelClassName,
					})}
				>
					{subLabelIcon && <FAIcon icon={subLabelIcon} className={`mr-1 ${subLabelClassName}`} />}

					{subLabel}
				</p>
			)}
			<textarea
				{...register(name, registerOptions)}
				disabled={disabled}
				id={id}
				className={`block w-full py-1 px-2 focus:ring-primary focus:border-primary-500 disabled:bg-gray-200 disabled:cursor-not-allowed ${baselineClassName} ${className}`}
				name={name}
				onKeyDown={onKeyDown ? (event) => onKeyDown(event) : null}
				rows={rows ? rows : 3}
				defaultValue={defaultValue}
				placeholder={placeholder}
				autoFocus={autoFocus}
				autoComplete={autoComplete}
				required={required}
			/>
			{errorMessage && <ErrorMessage errorMessage={errorMessage} />}
		</div>
	);
};
